import type { SlotImage, ImageKind } from '../types';

const ACCEPTED_MIMES = new Set(['image/jpeg', 'image/png', 'image/svg+xml'] as const);
type AcceptedMime = SlotImage['mime'];

export type LoadResult =
  | { ok: true; image: SlotImage }
  | { ok: false; reason: 'unsupported-type' | 'load-failed'; filename: string };

function readNaturalSize(objectUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = (e) => reject(e);
    img.src = objectUrl;
  });
}

export async function loadImageFile(file: File): Promise<LoadResult> {
  if (!ACCEPTED_MIMES.has(file.type as AcceptedMime)) {
    return { ok: false, reason: 'unsupported-type', filename: file.name };
  }
  const mime = file.type as AcceptedMime;
  const kind: ImageKind = mime === 'image/svg+xml' ? 'vector' : 'raster';
  const objectUrl = URL.createObjectURL(file);

  if (kind === 'vector') {
    return {
      ok: true,
      image: {
        filename: file.name,
        mime,
        kind,
        naturalWidth: null,
        naturalHeight: null,
        blob: file,
        objectUrl,
      },
    };
  }

  try {
    const { width, height } = await readNaturalSize(objectUrl);
    return {
      ok: true,
      image: {
        filename: file.name,
        mime,
        kind,
        naturalWidth: width,
        naturalHeight: height,
        blob: file,
        objectUrl,
      },
    };
  } catch {
    URL.revokeObjectURL(objectUrl);
    return { ok: false, reason: 'load-failed', filename: file.name };
  }
}
